import { createSlice } from '@reduxjs/toolkit'
import { REHYDRATE } from 'redux-persist'
import { includes, keyBy, isEmpty, isNil } from 'lodash-es'

/*
 * services and customizations field are normalized:
 *
 * services format example: {
 *  'id_1': { serviceId: 'id_1', count: 1 },
 *  'id_2': { serviceId: 'id_2', count: 3 },
 * }
 *
 * customizations format example: {
 *  'customization_id1': {
 *    'id_1': { itemId: 'id_1', count: 1 },
 *    'id_2': { itemId: 'id_2', count: 3 },
 *  },
 *  'customization_id2': {
 *    'id_1': { itemId: 'id_1', count: 1 },
 *    'id_2': { itemId: 'id_2', count: 3 },
 *  }
 * }
 */

const initialState = {
  classificationId: '',
  name: '',
  phone: '',
  email: '',
  services: {},
  address: {
    county: '',
    district: '',
    details: '',
    hasElevator: false,
  },
  staffNumber: '',
  customizations: {},
  siteImages: {},
  paymentInfo: {
    method: 'cash',
  },
  note: '',
  expectServiceTimes: [],
  customerBudget: '',

  // 自建單欄位
  verificationPhone: '',
  objectNo: '',
  selfBookingInfo: {
    reason: '',
    // fromUserBookedReservation: null, // 無輸入時不存在
    copyOpt: {
      mode: 'none',
      times: 0,
    },
  },
  selectedServicePackageInfo: {},
  // for unordered reservation
  contactDate: '',
}

export const bookFormSlice = createSlice({
  name: 'bookForm',
  initialState: initialState,
  reducers: {
    initForm: (state, action) => {
      const {
        services,
        classification,
        customizations,
        currentForm,
      } = action.payload
      let form = state
      if (state.classificationId !== classification._id) {
        // remove all except user data
        form = {
          ...initialState,
          classificationId: classification._id,
          name: state.name,
          phone: state.phone,
          email: state.email,
          address: state.address,
          vatNo: state.vatNo,
          vatTitle: state.vatTitle,
          objectNo: state.objectNo,
          staffNumber: state.staffNumber,
          note: state.note,
        }
        if (classification.mode === 'relocation') {
          form.moveInAddress = [{ type: 'main' }]
        }

        form.customizations = (customizations || [])
          .filter((customization) => customization.selectType === 'single')
          .map((customization) => ({
            [customization._id]: {
              [customization.items?.[0]?._id]: {
                itemId: customization.items?.[0]?._id,
                count: 1,
              },
            },
          }))
          .reduce((result, obj) => ({ ...result, ...obj }), {})
      }
      return form
    },
    clearForm: (state, action) => {
      return { ...initialState }
    },
    resetForm: (state, action) => {
      return {
        ...initialState,
        name: state.name,
        phone: state.phone,
        email: state.email,
        address: state.address,
        vatNo: state.vatNo,
        vatTitle: state.vatTitle,
      }
    },
    // TODO: 移除此action，
    // 這類action會導致參數被不知道哪裡來的事件修改而難以追蹤
    fillInForm: (state, action) => {
      const payload = action?.payload || {}
      return { ...state, ...payload }
    },
    initCustomizations: (state, action) => {
      action.payload.forEach((customization) => {
        if (
          customization.selectType === 'single' &&
          !state.customizations[customization._id]
        ) {
          state.customizations[customization._id] = {
            [customization.items?.[0]?._id]: {
              itemId: customization.items?.[0]?._id,
              count: 1,
            },
          }
        }
      })
    },

    setCounty: (state, action) => {
      state.address.county = action.payload
    },
    setDistrict: (state, action) => {
      state.address.district = action.payload
    },

    // handle service modification
    addOneService: (state, action) => {
      const service = action.payload
      if (state.services[service?._id]) {
        state.services[service?._id].count += 1
      } else {
        state.services[service?._id] = { serviceId: service?._id, count: 1 }
      }
    },
    removeOneService: (state, action) => {
      const service = action.payload
      if (state.services[service?._id]?.count <= 1) {
        delete state.services[service?._id]
      } else {
        state.services[service?._id].count -= 1
      }
    },
    toggleService: (state, action) => {
      const service = action.payload
      if (state.services[service?._id]) {
        delete state.services[service?._id]
      } else {
        state.services[service?._id] = { serviceId: service?._id, count: 1 }
      }
    },
    replaceExistingServices: (state, action) => {
      const service = action.payload
      state.services = {
        [service?._id]: { serviceId: service?._id, count: 1 },
      }
      state.customizations = {}
      state.selectedServicePackageInfo = service?.packageInfo
    },

    setBookedSession: (state, action) => {
      state.bookedSession = action.payload
    },
    setExpectServiceTimeOne: (state, action) => {
      if (!state?.bookedSession?.from) {
        state.bookedSession = action.payload
      } else {
        state.expectServiceTimes[0] = action.payload
      }
    },
    setExpectServiceTimeTwo: (state, action) => {
      if (!state?.bookedSession?.from) {
        state.bookedSession = action.payload
      } else if (!state.expectServiceTimes?.[0]?.from) {
        state.expectServiceTimes[0] = action.payload
      } else {
        state.expectServiceTimes[1] = action.payload
      }
    },
    cancelBookedSession: (state, action) => {
      if (!isEmpty(state.expectServiceTimes)) {
        const [first, ...rest] = state.expectServiceTimes
        state.bookedSession = first
        state.expectServiceTimes = rest
      } else {
        state.bookedSession = null
      }
    },
    cancelExpectServiceTimeOne: (state, action) => {
      if (state.expectServiceTimes?.[1]) {
        const [first, ...rest] = state.expectServiceTimes
        state.expectServiceTimes = rest
      } else {
        state.expectServiceTimes = []
      }
    },
    cancelExpectServiceTimeTwo: (state, action) => {
      const [first, second, ...rest] = state.expectServiceTimes
      state.expectServiceTimes = [first, ...rest]
    },

    // handle customization modification
    addOneCustomization: (state, action) => {
      const { item, customizationId } = action.payload
      if (!state.customizations[customizationId]) {
        state.customizations[customizationId] = {}
      }

      if (state.customizations[customizationId][item._id]) {
        state.customizations[customizationId][item._id].count += 1
      } else {
        state.customizations[customizationId][item._id] = {
          itemId: item._id,
          count: 1,
        }
      }
    },
    removeOneCustomization: (state, action) => {
      const { item, customizationId } = action.payload
      if (state.customizations?.[customizationId]?.[item._id]?.count <= 1) {
        if (
          state.customizations?.[customizationId]?.[item._id]?.customizedValue
        ) {
          state.customizations[customizationId][item._id].count = 0
        } else {
          delete state.customizations[customizationId][item._id]
        }

        if (isEmpty(state.customizations[customizationId])) {
          delete state.customizations[customizationId]
        }
      } else {
        state.customizations[customizationId][item._id].count -= 1
      }
    },
    toggleCustomization: (state, action) => {
      const { item, customizationId } = action.payload
      if (state.customizations?.[customizationId]?.[item._id]) {
        delete state.customizations[customizationId][item._id]
        if (isEmpty(state.customizations[customizationId])) {
          delete state.customizations[customizationId]
        }
      } else {
        if (!state.customizations[customizationId]) {
          state.customizations[customizationId] = {}
        }
        state.customizations[customizationId][item._id] = {
          itemId: item._id,
          count: 1,
        }
      }
    },
    replaceExistingCustomizations: (state, action) => {
      const { item, customizationId, text } = action.payload
      // Remove unselected items, but keep the customizedValue even unselected
      for (const itemId in state.customizations[customizationId]) {
        if (itemId !== item._id) {
          if (state.customizations[customizationId][itemId].customizedValue) {
            state.customizations[customizationId][itemId].count = 0
          } else {
            delete state.customizations[customizationId][itemId]
          }
        }
      }

      if (!state.customizations[customizationId]) {
        state.customizations[customizationId] = {}
      }
      state.customizations[customizationId][item._id] = {
        itemId: item._id,
        count: 1,
      }
      if (text) {
        state.customizations[customizationId][item._id].customizedValue = text
      }
    },
    changeCustomizationTextValue: (state, action) => {
      const { item, customizationId, text } = action.payload
      if (!state.customizations[customizationId]) {
        state.customizations[customizationId] = {}
      }
      if (!state.customizations[customizationId]?.[item._id]) {
        state.customizations[customizationId][item._id] = {
          itemId: item._id,
          count: 0,
          customizedValue: text,
        }
      } else {
        state.customizations[customizationId][item._id].customizedValue = text
      }
    },
    changeCustomizationDateValue: (state, action) => {
      const { item, customizationId, date } = action.payload
      if (!state.customizations[customizationId]) {
        state.customizations[customizationId] = {}
      }
      if (!state.customizations[customizationId]?.[item._id]) {
        state.customizations[customizationId][item._id] = {
          itemId: item._id,
          count: 1,
          customizedValue: date,
        }
      } else {
        if (date) {
          state.customizations[customizationId][item._id].customizedValue = date
        } else {
          delete state.customizations[customizationId][item._id]
        }
      }
    },
    removeEmptyCustomizationItem: (state, action) => {
      for (const customizationId in state.customizations) {
        for (const itemId in state.customizations[customizationId]) {
          if (!state.customizations[customizationId][itemId].count) {
            delete state.customizations[customizationId][itemId]
          }
        }

        if (isEmpty(state.customizations[customizationId])) {
          delete state.customizations[customizationId]
        }
      }
    },
    removeUnselectedCustomizationItem: (state, action) => {
      const itemKeyToServiceKeyMapping = action.payload

      const selectedServicesKey = Object.keys(state.services)
      for (const customizationId in state.customizations) {
        for (const itemId in state.customizations[customizationId]) {
          const serviceKey = itemKeyToServiceKeyMapping[itemId]
          if (selectedServicesKey.indexOf(serviceKey) < 0) {
            delete state.customizations[customizationId][itemId]
          }
        }
      }
    },

    setBookName: (state, action) => {
      state.name = action.payload
    },
    setCustomerBudget: (state, action) => {
      state.customerBudget = action.payload
    },
    setBookPhone: (state, action) => {
      state.phone = action.payload
    },
    setBookAddressDetails: (state, action) => {
      state.address.details = action.payload
    },
    setBookAddressHasElevator: (state, action) => {
      state.address.hasElevator = action.payload
    },
    setBookEmail: (state, action) => {
      state.email = action.payload
    },
    setVat: (state, action) => {
      const { vatNo, vatTitle } = action.payload
      state.vatNo = vatNo
      state.vatTitle = vatTitle
    },
    clearVat: (state, action) => {
      state.vatNo = ''
      state.vatTitle = ''
    },
    setBookNote: (state, action) => {
      state.note = action.payload
    },
    setSiteImages: (state, action) => {
      state.siteImages = keyBy(action.payload, (image) => image._id)
    },
    setCoupon: (state, action) => {
      state.coupon = action.payload
    },
    setPaymentInfo: (state, action) => {
      state.paymentInfo = action.payload
    },

    // Self Construct
    setVerificationPhone: (state, action) => {
      state.verificationPhone = action.payload
    },
    setStaffNumber: (state, action) => {
      state.staffNumber = action.payload
    },
    setObjectNo: (state, action) => {
      state.objectNo = action.payload
    },
    setSelfBookingReason: (state, action) => {
      state.selfBookingInfo.reason = action.payload
    },
    setSelfBookingCopyMode: (state, action) => {
      state.selfBookingInfo.copyOpt.mode = action.payload
      if (action.payload === 'none') {
        state.selfBookingInfo.copyOpt.times = 0
      }
    },
    setSelfBookingCopyTimes: (state, action) => {
      state.selfBookingInfo.copyOpt.times = parseInt(action.payload)
    },

    setReferrer: (state, action) => {
      state.referrer = action.payload
    },

    // move in addresses control
    addMoveInAddress: (state, action) => {
      state.moveInAddress.push({ type: 'others' })
    },
    removeMoveInAddressByIndex: (state, action) => {
      state.moveInAddress.splice(action.payload, 1)
    },
    updateMoveInAddressByIndex: (state, action) => {
      const { index, address } = action.payload
      state.moveInAddress = state.moveInAddress.map((addressInfo, idx) =>
        idx === index ? address : addressInfo
      )
    },
    replaceMoveInAddress: (state, action) => {
      state.moveInAddress = action.payload
    },

    // unordered reservation
    setContactDate: (state, action) => {
      state.contactDate = action.payload
    },
  },

  extraReducers: (builder) => {
    builder.addCase(REHYDRATE, (state, action) => {
      // TODO: control rehydration here

      // migrate old persist data without paymentInfo
      const newState = action.payload
      if (newState?.bookForm && !newState.bookForm?.paymentInfo) {
        newState.bookForm.paymentInfo = initialState.paymentInfo
      }
      // migrate expectServiceTimes if none
      if (newState?.bookForm && !newState.bookForm?.expectServiceTimes) {
        newState.bookForm.expectServiceTimes = initialState.expectServiceTimes
      }

      // migrate selfBookingInfo if none
      if (newState?.bookForm && !newState.bookForm?.selfBookingInfo) {
        newState.bookForm.selfBookingInfo = initialState.selfBookingInfo
      }
    })
  },
})

export const selectBookForm = (state) => state.bookForm

export const {
  initForm,
  resetForm,
  fillInForm,
  clearForm,
  initCustomizations,
  setCounty,
  setDistrict,
  addOneService,
  removeOneService,
  toggleService,
  replaceExistingServices,
  setBookedSession,
  setExpectServiceTimeOne,
  setExpectServiceTimeTwo,
  cancelBookedSession,
  cancelExpectServiceTimeOne,
  cancelExpectServiceTimeTwo,
  addOneCustomization,
  removeOneCustomization,
  toggleCustomization,
  replaceExistingCustomizations,
  changeCustomizationTextValue,
  changeCustomizationDateValue,
  removeEmptyCustomizationItem,
  removeUnselectedCustomizationItem,
  setBookName,
  setCustomerBudget,
  setBookPhone,
  setBookAddressDetails,
  setBookAddressHasElevator,
  setBookEmail,
  setVat,
  clearVat,
  setBookNote,
  setSiteImages,
  setCoupon,
  setPaymentInfo,
  setVerificationPhone,
  setStaffNumber,
  setObjectNo,
  setSelfBookingReason,
  setSelfBookingCopyMode,
  setSelfBookingCopyTimes,
  setReferrer,
  addMoveInAddress,
  removeMoveInAddressByIndex,
  updateMoveInAddressByIndex,
  replaceMoveInAddress,
  setContactDate,
} = bookFormSlice.actions

export default bookFormSlice.reducer
